import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceDashboard, apiServiceInstanceDashboardWithWaitTime } from '../apiUtils';
import {
  FetchGroupingPayload,
  CreateJourneyElementPayload,
  RegeneratePayload,
  FetchElementPayload,
  JourneyStage,
  ConsumerJourneyFilterObject,
} from '@/types/consumerJourney';

// Delete a journey stage element
export const deleteJourneyElement = createAsyncThunk(
  'consumerJourney/deleteElement',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    reportElementId: number;
  }) => {
    const res = await apiServiceInstanceDashboard.delete('/report/elements/', {
      data: payload,
    });
    return { ...res.data, deletedElementId: payload.reportElementId };
  },
);

// Reorder a journey stage element
export const reorderJourneyElement = createAsyncThunk(
  'consumerJourney/reorderElement',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    reportElementId: number;
    orderId: number;
  }) => {
    const res = await apiServiceInstanceDashboard.post(
      '/report/reorderelement/',
      payload,
    );
    return res.data;
  },
);

// Merge two journey stages into one (adds questions from source to target, user can delete source manually)
export const mergeJourneyStages = createAsyncThunk(
  'consumerJourney/mergeStages',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    sourceElementId: number;
    targetElementId: number;
    mergedStageName: string;
    mergedQuestionIds: number[];
    scaleType: string;
    stageId?: string;
    userPreference?: string;
  }) => {
    // Update target element with merged questions (source element kept for user to delete manually)
    const updateRes = await apiServiceInstanceDashboard.put('/report/elements/', {
      projectId: payload.projectId,
      batchId: payload.batchId,
      reportId: payload.reportId,
      reportPageId: payload.reportPageId,
      reportElementId: payload.targetElementId,
      filterObject: {
        batchId: payload.batchId,
        stageId: payload.stageId || `merged-${Date.now()}`,
        stageName: payload.mergedStageName,
        questionIds: payload.mergedQuestionIds,
        scaleType: payload.scaleType,
        userPreference: payload.userPreference || '',
        age: [],
        gender: [],
        city: [],
        state: [],
        country: [],
        questionObject: [],
      },
      filterType: 'consumer_journey',
      title: payload.mergedStageName,
      titleStyle: {},
      description: '',
      descriptionStyle: {},
      notes: '',
      notesStyle: {},
      elementType: 'consumer_journey_stage',
      elementStyle: {},
      layoutType: 'full_width',
      layoutStyle: {},
    });

    return {
      ...updateRes.data,
      mergedElementId: payload.targetElementId,
      sourceElementId: payload.sourceElementId,
    };
  },
);

// Fetch AI-suggested question groupings (uses extended timeout for large datasets)
export const fetchConsumerJourneyGrouping = createAsyncThunk(
  'consumerJourney/fetchGrouping',
  async (payload: FetchGroupingPayload) => {
    const res = await apiServiceInstanceDashboardWithWaitTime.post(
      '/report/consumer-journey/grouping/',
      payload,
    );
    return res.data;
  },
);

// Create a single journey stage element
export const createJourneyElement = async (
  payload: CreateJourneyElementPayload & {
    questionObject?: Array<{
      questionId: number;
      answerType: string;
      answer: string[] | Record<string, string[]>;
    }>;
  },
) => {
  const filterObject: ConsumerJourneyFilterObject & { questionObject?: any[] } = {
    batchId: payload.batchId,
    stageId: payload.stage.stageId,
    stageName: payload.stage.stageName,
    questionIds: payload.stage.questions.map((q) => q.questionId),
    scaleType: payload.stage.scaleType || '0-10',
    userPreference: payload.stage.userPreference || '',
    age: [],
    gender: [],
    city: [],
    state: [],
    country: [],
    questionObject: payload.questionObject || [],
  };

  const res = await apiServiceInstanceDashboard.post('/report/elements/', {
    projectId: payload.projectId,
    batchId: payload.batchId,
    reportId: payload.reportId,
    reportPageId: payload.reportPageId,
    orderId: payload.orderId,
    filterType: 'consumer_journey',
    filterObject,
    title: payload.stage.stageName,
    titleStyle: {},
    notes: '',
    notesStyle: {},
    elementType: 'consumer_journey_stage',
    elementStyle: {},
    layoutType: 'full_width',
    layoutStyle: {},
  });

  return res.data;
};

// Create all journey stage elements in parallel
export const createAllJourneyElements = createAsyncThunk(
  'consumerJourney/createAllElements',
  async (
    payload: {
      projectId: number;
      batchId: number;
      reportId: number;
      reportPageId: number;
      stages: JourneyStage[];
      userId: number;
      session?: string;
      startOrderId?: number; // Starting order ID for new elements
      questionObject?: Array<{
        questionId: number;
        answerType: string;
        answer: string[] | Record<string, string[]>;
      }>;
    },
    { dispatch },
  ) => {
    const results = [];
    const startOrder = payload.startOrderId || 1;

    // Create elements in parallel
    const promises = payload.stages.map((stage, index) =>
      createJourneyElement({
        projectId: payload.projectId,
        batchId: payload.batchId,
        reportId: payload.reportId,
        reportPageId: payload.reportPageId,
        stage,
        orderId: startOrder + index,
        userId: payload.userId,
        session: payload.session,
        questionObject: payload.questionObject,
      }),
    );

    const responses = await Promise.all(promises);
    return responses;
  },
);

// Regenerate AI analysis for a stage
export const regenerateStageAnalysis = createAsyncThunk(
  'consumerJourney/regenerate',
  async (payload: RegeneratePayload) => {
    const res = await apiServiceInstanceDashboard.post(
      '/report/consumer-journey/regenerate/',
      payload,
    );
    return res.data;
  },
);

// Fetch a single journey element data
export const fetchJourneyElement = async (payload: FetchElementPayload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/report/getelements/',
    payload,
  );
  return res.data;
};

// Fetch all journey elements for a page
export const fetchAllJourneyElements = createAsyncThunk(
  'consumerJourney/fetchAllElements',
  async (
    payload: {
      projectId: number;
      batchId: number;
      reportId: number;
      reportPageId: number;
      elementIds: number[];
      userId: number;
      session?: string;
    },
  ) => {
    const promises = payload.elementIds.map((elementId) =>
      fetchJourneyElement({
        projectId: payload.projectId,
        batchId: payload.batchId,
        reportId: payload.reportId,
        reportPageId: payload.reportPageId,
        reportElementId: elementId,
        userId: payload.userId,
        session: payload.session,
      }),
    );

    const responses = await Promise.all(promises);
    return responses;
  },
);
